import { ChangeEventHandler, MouseEventHandler, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { S3ProviderListOutputItem } from "@aws-amplify/storage";
import { Tooltip, LinearProgress } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Trans, useTranslation } from "react-i18next";

import Footer from "../../components/Footer";
import DFNavbar from "../../components/Navbar";
import { MYPAGE_ROUTE } from "../../consts/routes";
import { CheckmarkCircleIcon, CrossCircleIcon } from "../../components/Icons";
import { ScrollToTop } from "../../components/ScrollToTop";
import {
    createFolder,
    extractFilesByProject,
    extractProjects,
    getFiles,
    getFileList,
    ifExistInSpecifiedProject,
    putFile,
    removeFile,
} from "../../clients/S3Client";
import { useUserContext } from "../../contexts/UserContext";
import { getUser } from "../../clients/UserClient";
import { PROJECT_NAME_MAX_LENGTH } from "../../consts/constants";
import { formatDateTime } from "../../utils/DateUtils";
import S3FileDeleteConfirmModal from "../modals/S3FileDeleteConfirmModal";
import S3FilesDeleteConfirmModal from "../modals/S3FilesDeleteConfirmModal";
import ContextMenu from "./ContextMenu";
import FileUploader from "./FileUploader";
import TiffConvertConfirmModal from "../modals/TiffConvertConfirmModal";
import ShareProjectFileModal from "../modals/ShareProjectFileModal";
import ShareFileModal from "../modals/ShareFileModal";
import TransfarFileBetweenProjectsModal from "../modals/TransfarFileBetweenProjectsModal";
import CheckStorageUsageModal from "../modals/CheckStorageUsageModal";
import { invokeTiffConvert } from "../../clients/TiffConvertClient";
import { Helmet } from "react-helmet";
import { moveFile } from "../../clients/S3Client";

const NEW_PROJECT = "new";

interface HandleUploadProps {
    acceptedFileList: File[];
    setAcceptedFileList: React.Dispatch<React.SetStateAction<File[]>>;
}

/**
 * データ管理
 * @returns JSX.Element
 */
const DataManagement = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, getRecentToken } = useUserContext();
    const [fileList, setFileList] = useState<S3ProviderListOutputItem[]>([]);
    const [filteredFileList, setFilteredFileList] = useState<S3ProviderListOutputItem[]>([]);
    const [projectList, setProjectList] = useState<string[]>([]);
    const [selectedProject, setSelectedProject] = useState<string>("");
    const [newProjectName, setNewProjectName] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<S3ProviderListOutputItem>();
    const [totalSize, setTotalSize] = useState<number>(0);

    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [uploadProgress, setUploadProgress] = useState<number>(0);

    // S3ファイル共有モーダル
    const [showShareFileModal, setShowShareFileModal] = useState<boolean>(false);

    // プロジェクト間ファイル移動
    const [showTransfarFileBetweenProjectsModal, setShowTransfarFileBetweenProjectsModal] = useState<boolean>(false);
    const [transfarModalErrorMessage, setTransfarModalErrorMessage] = useState<string>("");

    // S3削除モーダル
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteModalErrorMessage, setDeleteModalErrorMessage] = useState<string>("");

    // TIFF変換確認モーダル
    const [showTiffConvertModal, setShowTiffConvertModal] = useState<boolean>(false);
    const [tiffModalErrorMessage, setTiffModalErrorMessage] = useState<string>("");
    const [resizeDisabled, setResizeDisabled] = useState<boolean>(false);
    const [tiling, setTiling] = useState<boolean>(false);

    // 操作メニュー関連
    const [showMenu, setShowMenu] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
    const menuRef = useRef<any>(null);

    // プロジェクトを共有モーダル
    const [showShareProjectFileModal, setShowShareProjectFileModal] = useState<boolean>(false);

    // CheckStorageUsage用モーダル
    const [showCheckStorageUsageModal, setShowCheckStorageUsageModal] = useState<boolean>(false);
    const [userStorageMaxSizeGB, setUserStorageMaxSizeGB] = useState<number>(0);

    // チェックボックスの選択とドロップダウンボタン(Action)
    const [allChecked, setAllChecked] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<S3ProviderListOutputItem[]>([]);
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [selectedAction, setSelectedAction] = useState<string>("-");
    const actionMenuRef = useRef<any>(null);

    // S3複数ファイル削除モーダル
    const [showDeleteFilesModal, setShowDeleteFilesModal] = useState<boolean>(false);
    const [deleteFilesModalErrorMessage, setDeleteFilesModalErrorMessage] = useState<string>("");

    /**
     * S3からファイルリストの取得
     * @returns
     */
    async function fetchFileList() {
        setLoading(true);
        try {
            // ファイル一覧の取得
            const list: S3ProviderListOutputItem[] = await getFileList(user.attributes.email);
            setFileList(list);
            // プロジェクトの抽出
            const pList = await extractProjects(list);
            setProjectList(pList);
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setLoading(false);
        }
    }

    /**
     * S3からファイルをダウンロード
     * @param file
     * @returns
     */
    async function downloadFile(file: S3ProviderListOutputItem) {
        if (!file.key) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        }

        const fileKeys = [file.key];
        getFiles(fileKeys)
            .then(() => console.log("Download requested:"))
            .catch((error) => {
                setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            });
    }

    /**
     * S3からファイルを削除
     * @param file
     * @returns
     */
    async function deleteFile(file: S3ProviderListOutputItem) {
        setIsProcessing(true);
        setDeleteModalErrorMessage("");
        try {
            if (file.key) {
                await removeFile(file.key);
                // ※状態反映待機の１秒のインターバル
                await new Promise((resolve) => setTimeout(resolve, 1000));
                // 表示中のファイルリストから対象ファイルを削除
                setFilteredFileList(filteredFileList.filter((f) => f.key != file.key));
                // メモリ中のファイルリスト更新
                await fetchFileList();
            }
            setShowDeleteModal(false);
        } catch (error) {
            setDeleteModalErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setIsProcessing(false);
        }
    }

    /**
     * S3からフォルダを削除
     * @param file
     * @returns
     */
    async function deleteFolder() {
        setLoading(true);
        try {
            await removeFile(user.attributes.email + "/" + selectedProject + "/");
            // ※状態反映待機の１秒のインターバル
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setSelectedProject("");
            await fetchFileList();
            setSuccessMessage(t("DataManagement.proccessSuccessed"));
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setLoading(false);
        }
    }

    /**
     * S3から複数ファイルを削除
     * @param files
     * @returns
     */
    async function deleteFiles(files: S3ProviderListOutputItem[]) {
        setIsProcessing(true);
        setDeleteFilesModalErrorMessage("");
        try {
            for (const file of files) {
                if (file.key) {
                    await removeFile(file.key);
                }
            }
            // ※状態反映待機の１秒のインターバル
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // 表示中のファイルリストから対象のファイルを削除
            setFilteredFileList(filteredFileList.filter((f) => !files.some((file) => f.key === file.key)));
            // メモリ中のファイルリスト更新
            await fetchFileList();
            setShowDeleteFilesModal(false);
        } catch (error) {
            setDeleteFilesModalErrorMessage(t("ErrorMessage.failToConnectStorage"));
        } finally {
            setIsProcessing(false);
            setSelectedFiles([]);
        }
    }

    /**
     * 選択されたファイルを一括ダウンロード
     */
    const handleDownloadSelectedFiles = () => {
        if (selectedFiles.length === 0) {
            console.warn("No files selected for download.");
            return;
        }
        const fileKeys = selectedFiles.map((file) => file.key).filter(Boolean) as string[];
        getFiles(fileKeys)
            .then(() => console.log("Download requested:"))
            .catch((error) => {
                setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            });
    };

    /**
     * ファイルを別プロジェクトへ移動
     * @param selectedToProject
     * @returns
     */
    async function moveFileToOtherProject(selectedToProject: string, credentials: any) {
        setIsProcessing(true);
        setTransfarModalErrorMessage("");
        try {
            if (!selectedFile || !selectedFile.key) {
                setErrorMessage(t("ErrorMessage.systemErrorMessage"));
                return;
            }
            var targetFileName = selectedFile.key.split("/").pop();
            // 移動先に同じ名前のファイルがいるか確認
            if (ifExistInSpecifiedProject(fileList, selectedToProject, targetFileName)) {
                setTransfarModalErrorMessage(t("TransfarFileModal.sameNameExists"));
                return;
            }
            // ファイル移動
            await moveFile(
                user.attributes.email + "/" + selectedProject + "/" + targetFileName,
                user.attributes.email + "/" + selectedToProject + "/" + targetFileName,
                credentials,
            );
            // ※状態反映待機の１秒のインターバル
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // 表示中のファイルリストから対象ファイルを削除
            setFilteredFileList(filteredFileList.filter((f) => f.key != selectedFile.key));
            // メモリ中のファイルリスト更新
            await fetchFileList();

            setSuccessMessage(t("TransfarFileModal.proccessSuccessed"));
            setShowTransfarFileBetweenProjectsModal(false);
        } catch (error) {
            setTransfarModalErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setIsProcessing(false);
        }
    }

    /**
     * TIFF変換処理を呼び出し
     * @param file
     * @returns
     */
    async function convertTiff(file: S3ProviderListOutputItem) {
        setIsProcessing(true);
        setTiffModalErrorMessage("");
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setTiffModalErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API] TIFF変換呼び出しAPI
            if (file.key) {
                await invokeTiffConvert(idToken, file.key, resizeDisabled, tiling);
            }
            setSuccessMessage(t("DataManagement.requestAccepted"));
            window.scrollTo(0, 0); // ページトップへ
            setShowTiffConvertModal(false);
        } catch (error) {
            setTiffModalErrorMessage(t("ErrorMessage.failToSendRequest"));
            return;
        } finally {
            setIsProcessing(false);
        }
    }

    /**
     * TIFF画像変換モーダルを開く。
     * @param resizeDisabled リサイズを行うか
     * @param tiling タイル化を行うか
     */
    async function openShowTiffConvertModal(resizeDisabled: boolean, tiling: boolean) {
        setResizeDisabled(resizeDisabled);
        setTiling(tiling);
        setShowTiffConvertModal(true);
    }

    /**
     * TIFF画像変換モーダルを閉じる。
     */
    async function closeShowTiffConvertModal() {
        setResizeDisabled(false);
        setTiling(false);
        setShowTiffConvertModal(false);
    }

    /**
     * Getting info of DB user
     * @param file
     * @returns
     */
    async function fetchDBUser() {
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }
            // [API]ユーザー情報（自身）の取得
            const userResponse = await getUser(idToken, user.attributes.sub);
            if (userResponse) {
                setUserStorageMaxSizeGB(userResponse.storage_size);
            }
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        }
    }

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);
    useEffect(() => {
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        fetchDBUser();
        if (!effectRun.current) {
            fetchFileList();
            return () => {
                effectRun.current = true;
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    /**
     * ストレージ使用量を算出
     */
    useEffect(() => {
        if (fileList) {
            const size: number = fileList.reduce((acc, file) => acc + (file.size ? file.size : 0), 0);
            setTotalSize(size);
        }
    }, [fileList]);

    useEffect(() => {
        if (selectedFiles.length === 0) {
            setSelectedAction("-");
            setDropdownVisible(false);
        }
    }, [selectedFiles]);

    /**
     * ドロップダウンボタン
     */
    const toggleDropdown = () => {
        if (selectedFiles.length > 0) {
            setDropdownVisible(!dropdownVisible);
        }
    };

    /**
     * ドロップダウンの処理
     */
    const handleActionSelect = (action: string) => {
        if (action === t("DataManagement.dropboxDefault")) {
            console.log(t("DataManagement.dropboxDefault"));
        } else if (action === t("DataManagement.drobpoxDelete")) {
            setShowDeleteFilesModal(true);
        } else if (action === t("DataManagement.drobpoxDownload")) {
            handleDownloadSelectedFiles();
        }
        setDropdownVisible(false);
    };

    /**
     * ヘッダのチェックボックスを制御
     */
    const handleHeaderCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        setAllChecked(isChecked);
        setSelectedFiles(isChecked ? [...filteredFileList] : []);
    };

    /**
     * チェックボックスの処理
     */
    const handleFileSelection = (file: S3ProviderListOutputItem, isSelected: boolean) => {
        setSelectedFiles((prevSelected) => {
            let newSelected;
            if (isSelected) {
                newSelected = [...prevSelected, file];
            } else {
                newSelected = prevSelected.filter((f) => f.key !== file.key);
            }

            // すべて選択されたらヘッダーのチェックボックスをON そうでなければOFF
            setAllChecked(newSelected.length === filteredFileList.length && filteredFileList.length > 0);
            return newSelected;
        });
    };

    /**
     * プロジェクト選択時のイベント処理
     */
    const handleProjectChange: ChangeEventHandler<HTMLSelectElement> = (event) => {
        setSuccessMessage("");
        setErrorMessage("");
        const pname: string = event.target.value;
        setSelectedProject(pname);
        const newList = extractFilesByProject(fileList, pname);
        setFilteredFileList(newList);
        setAllChecked(false);
        setSelectedFiles([]);
    };

    /**
     * 新規プロジェクト名の入力イベント
     * @param event
     */
    const handleProjectNameChange: ChangeEventHandler<HTMLInputElement> = (event) => {
        const inputValue = event.target.value;
        setNewProjectName(inputValue);
        if (/\s/.test(inputValue)) {
            setErrorMessage(t("DataManagement.cannotContainSpaces"));
        } else {
            setErrorMessage("");
        }
    };

    /**
     * 新規プロジェクト登録押下時のイベント処理
     * @param event
     * @returns
     */
    const handleRegisterProject: MouseEventHandler<HTMLButtonElement> = async (event) => {
        setLoading(true);
        setErrorMessage("");
        try {
            // バリデーションチェック
            if (!newProjectName) {
                setErrorMessage(t("DataManagement.enterProjectName"));
                return;
            } else if (/\s/.test(newProjectName)) {
                setErrorMessage(t("DataManagement.cannotContainSpaces"));
                return;
            }
            // フォルダ作成
            await createFolder(user.attributes.email, newProjectName);
            // ※状態反映待機の１秒のインターバル
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // 選択状態初期化＆ファイル一覧再取得
            setNewProjectName("");
            setSelectedProject("");
            await fetchFileList();
            setSuccessMessage(t("DataManagement.proccessSuccessed"));
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setLoading(false);
        }
    };

    /**
     * ファイルアップロード処理
     * @returns
     */
    const handleUpload = async ({ acceptedFileList, setAcceptedFileList }: HandleUploadProps) => {
        setUploading(true);
        setUploadProgress(0);

        // ファイルごとの進捗率を管理
        const progressArray = new Array(acceptedFileList.length).fill(0);

        const updateProgress = () => {
            // 全体の進捗率を計算
            const totalProgress = progressArray.reduce((sum, progress) => sum + progress, 0);
            const overallProgress = Math.round(totalProgress / acceptedFileList.length);
            setUploadProgress(overallProgress);
        };

        try {
            const uploadPromises = acceptedFileList.map((file, index) => {
                const key: string = user.attributes.email + "/" + selectedProject + "/" + file.name;

                return putFile(key, file, (progress) => {
                    progressArray[index] = progress; // ファイルごとの進捗を更新
                    updateProgress(); // 全体の進捗を更新
                });
            });
            await Promise.all(uploadPromises);

            // ※状態反映待機の１秒のインターバル
            await new Promise((resolve) => setTimeout(resolve, 1000));
            // 選択状態初期化＆ファイル一覧再取得
            setAcceptedFileList([]);
            setNewProjectName("");
            setSelectedProject("");
            await fetchFileList();
            setSuccessMessage(t("DataManagement.proccessSuccessed"));
        } catch (error) {
            setErrorMessage(t("ErrorMessage.failToConnectStorage"));
            return;
        } finally {
            setUploading(false);
        }
    };

    /**
     * ShareProject用モーダルを開く
     */
    const handleOpenShareProjectFileModal = () => {
        setShowShareProjectFileModal(true);
    };

    /**
     * ShareProject用モーダルを閉じる
     */
    const handleCloseShareProjectFileModal = () => {
        setShowShareProjectFileModal(false);
    };

    /**
     * ShareFile用モーダルを閉じる
     */
    const handleCloseShareFileModal = () => {
        setShowShareFileModal(false);
    };

    /**
     * TransfarFileBetweenProjectsModal用モーダルを閉じる
     */
    const handleCloseTransfarFileBetweenProjectsModal = () => {
        setShowTransfarFileBetweenProjectsModal(false);
    };

    /**
     * CheckStorageUsage用モーダルを開く
     */
    const handleOpenCheckStorageUsageModal = () => {
        if (!userStorageMaxSizeGB) {
            console.log("you did not have your db user info yet.");
            return;
        }
        setShowCheckStorageUsageModal(true);
    };

    /**
     * CheckStorageUsage用モーダルを閉じる
     */
    const handleCloseCheckStorageUsageModal = () => {
        setShowCheckStorageUsageModal(false);
    };

    /**
     * 操作メニューの押下
     * @param e
     */
    const setShowContextMenu = (e: any) => {
        setShowMenu(true);
        setMenuPosition({
            x: e.clientX + window.scrollX,
            y: e.clientY + window.scrollY,
        });
    };

    // クリックイベントがメニュー外で発生したか監視
    useEffect(() => {
        const handleOutsideClick = (event: any) => {
            if (showMenu && menuRef.current && !menuRef.current.contains(event.target)) {
                setShowMenu(false);
            } else if (dropdownVisible && actionMenuRef.current && !actionMenuRef.current.contains(event.target)) {
                setDropdownVisible(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [showMenu, dropdownVisible]);

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title={t("DataManagement.meta.title")} />
            <ScrollToTop />
            <DFNavbar bottomPadding={true} />
            <div className="flex-grow items-center justify-center px-6 py-8 pt-20 lg:px-20">
                <h1 className="h1-common py-3">{t("DataManagement.title")}</h1>

                {/* 成功メッセージ */}
                {successMessage && (
                    <>
                        <div className="flex items-center justify-center py-2">
                            <CheckmarkCircleIcon />
                            <span className="pl-2 text-center text-xl font-bold text-green-500">
                                {successMessage.split("\n").map((line, index) => (
                                    <div key={index}>{line}</div>
                                ))}
                            </span>
                        </div>
                        <p className="pb-2 text-center font-bold text-green-700">
                            {t("DataManagement.reloadAndCheck")}
                        </p>
                    </>
                )}
                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center py-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {loading && (
                    // ローディング
                    <div className="flex flex-col items-center justify-center p-10">
                        <div className="h-16 w-16 animate-spin rounded-full border-t-4 border-lime-800"></div>
                        <p className="text-gre-800 p-2 font-bold">Loading...</p>
                    </div>
                )}

                {/* アップロード中の進捗表示 */}
                {uploading && (
                    <div className="mx-auto max-w-lg items-center py-2">
                        <div className="mb-2 text-center">
                            <p className="text-lg font-semibold text-gray-600">
                                {t("DataManagement.uploadInProgress")}
                            </p>
                        </div>
                        <div className="flex items-center">
                            <div className="mr-2 w-[90%]">
                                <LinearProgress
                                    variant="determinate"
                                    value={uploadProgress}
                                    sx={{
                                        height: "30px",
                                        backgroundColor: "#d9d9d9", // Base color of a progress bar
                                        "& .MuiLinearProgress-bar": {
                                            backgroundColor: "#9fd77f", // Key color of the bar
                                        },
                                    }}
                                />
                            </div>
                            <div className="w-[10%] text-center">
                                <span className="text-lg font-bold text-gray-600">{uploadProgress}%</span>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && !uploading && (
                    <>
                        {/* ストレージ使用量を確認ボタン */}
                        <div className="mx-auto flex max-w-lg items-center justify-end text-right text-sm">
                            <a
                                href="#"
                                onClick={handleOpenCheckStorageUsageModal}
                                className="text-blue-500 underline hover:text-blue-700"
                            >
                                {t("DataManagement.checkStorageUsageLink")}
                            </a>
                        </div>

                        {/* プロジェクト選択 */}
                        <div className="mx-auto flex max-w-lg items-center justify-center py-2">
                            <select
                                name="project"
                                id="project"
                                className="textbox-common"
                                value={selectedProject}
                                onChange={handleProjectChange}
                            >
                                <option hidden>{t("DataManagement.selectProject")}</option>
                                {projectList.map((p, index) => (
                                    <option key={index} value={p}>
                                        {p}
                                    </option>
                                ))}
                                <option key={projectList.length} value={NEW_PROJECT}>
                                    {t("DataManagement.newProject")}
                                </option>
                            </select>
                        </div>

                        {/* 新規プロジェクト作成 */}
                        {selectedProject == NEW_PROJECT && (
                            <div className="mx-auto flex max-w-lg items-center justify-center py-2">
                                <input
                                    type="text"
                                    name="new-project"
                                    id="new-project"
                                    placeholder={t("CommonLabel.projectName")}
                                    className="textbox-common"
                                    onChange={handleProjectNameChange}
                                    maxLength={PROJECT_NAME_MAX_LENGTH}
                                    required
                                />
                                <button onClick={handleRegisterProject} className="btn-primary ml-2 w-20">
                                    {t("CommonLabel.register")}
                                </button>
                            </div>
                        )}

                        {/* プロジェクト選択時に表示 */}
                        {selectedProject && selectedProject != NEW_PROJECT && (
                            <div className="mx-auto max-w-3xl overflow-x-auto py-2 transition duration-500 ease-in-out">
                                {/* プロジェクトを共有ボタン */}
                                <div className="mb-10 mt-0.5 flex items-center justify-evenly">
                                    <button
                                        type="button"
                                        onClick={handleOpenShareProjectFileModal}
                                        className="h-9 w-[160px] rounded-lg border border-gray-400 bg-gray-100 py-2 text-center text-sm font-medium text-gray-600 hover:bg-gray-300 active:bg-gray-400 active:font-bold"
                                    >
                                        {t("DataManagement.shareProject")}
                                    </button>
                                </div>

                                {/* ファイルアップロード */}
                                <FileUploader
                                    totalSize={totalSize}
                                    handleUpload={handleUpload}
                                    userStorageMaxSizeGB={userStorageMaxSizeGB}
                                />

                                {downloading && (
                                    // ダウンロード中
                                    <div className="flex flex-col items-center justify-center pt-8">
                                        <div className="h-8 w-8 animate-spin rounded-full border-t-4 border-lime-800"></div>
                                        <p className="text-gre-800 p-2 font-bold">Downloading...</p>
                                    </div>
                                )}

                                <div className="grid grid-cols-3 items-center pt-5">
                                    {/* ドロップダウンボタン */}
                                    <div className="relative inline-block text-left">
                                        <button
                                            type="button"
                                            className={`mb-0.5 w-auto rounded-sm border border-gray-300 bg-gray-100 px-5 py-1.5 text-center text-sm text-gray-800 hover:bg-gray-200 active:bg-gray-300 ${
                                                selectedFiles.length === 0 ? "cursor-not-allowed opacity-50" : ""
                                            }`}
                                            onClick={toggleDropdown}
                                            disabled={selectedFiles.length === 0}
                                        >
                                            {t("DataManagement.actionButton")}
                                        </button>

                                        {/* ドロップダウンメニュー */}
                                        {dropdownVisible && (
                                            <div className="absolute z-10 mt-1 w-56 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                                                <ul
                                                    className="py-1"
                                                    role="menu"
                                                    aria-orientation="vertical"
                                                    aria-labelledby="dropdown-button"
                                                    ref={actionMenuRef}
                                                >
                                                    <li
                                                        className="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                        role="menuitem"
                                                        onClick={() =>
                                                            handleActionSelect(t("DataManagement.dropboxDefault"))
                                                        }
                                                    >
                                                        {t("DataManagement.dropboxDefault")}
                                                    </li>
                                                    <li
                                                        className="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                        role="menuitem"
                                                        onClick={() =>
                                                            handleActionSelect(t("DataManagement.drobpoxDelete"))
                                                        }
                                                    >
                                                        {t("DataManagement.drobpoxDelete")}
                                                    </li>
                                                    <li
                                                        className="block cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                                        role="menuitem"
                                                        onClick={() =>
                                                            handleActionSelect(t("DataManagement.drobpoxDownload"))
                                                        }
                                                    >
                                                        {t("DataManagement.drobpoxDownload")}
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {/* ファイル一覧処理 */}
                                    <div className="flex justify-center">
                                        <p className="text-center text-lg font-bold text-gray-600">
                                            {t("DataManagement.listFile")}
                                        </p>
                                        <Tooltip
                                            title={
                                                <ul>
                                                    <li>{t("DataManagement.listHelp1")}</li>
                                                </ul>
                                            }
                                            arrow
                                        >
                                            <HelpOutlineIcon
                                                fontSize="small"
                                                className="my-auto ml-1 cursor-pointer text-gray-500 hover:text-blue-500"
                                            />
                                        </Tooltip>
                                    </div>
                                </div>

                                <table className="min-w-full divide-y divide-gray-200 border">
                                    <thead className="bg-gray-200">
                                        <tr className={`text-sm font-semibold uppercase tracking-wide text-gray-700`}>
                                            {/* [ヘッダ] チェックボックス */}
                                            <th scope="col" className="whitespace-nowrap px-2 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    onChange={handleHeaderCheckboxChange}
                                                    checked={allChecked}
                                                />
                                            </th>
                                            {/* [ヘッダ] ファイル名 */}
                                            <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                                <div className="flex items-center gap-x-2">
                                                    <span>{t("CommonLabel.fileName")}</span>
                                                </div>
                                            </th>
                                            {/* [ヘッダ] 最終更新日時 */}
                                            <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                                <div className="flex items-center gap-x-2">
                                                    <span>{t("CommonLabel.lastModified")}</span>
                                                </div>
                                            </th>
                                            {/* [ヘッダ] サイズ */}
                                            <th scope="col" className="whitespace-nowrap px-6 py-2 text-left">
                                                <div className="flex items-center gap-x-2">
                                                    <span>{t("CommonLabel.dataSize")}</span>
                                                </div>
                                            </th>
                                            {/* [ヘッダ] 操作 */}
                                            <th scope="col" className="whitespace-nowrap px-2 py-2">
                                                <div className="text-center">
                                                    <span>{t("CommonLabel.operation")}</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-gray-200">
                                        {filteredFileList.map((file: any) => (
                                            <tr key={file.key} className="text-sm font-semibold text-gray-800">
                                                {/* チェックボックス */}
                                                <td className="h-px w-px whitespace-nowrap px-2 py-2 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedFiles.some(
                                                            (selected) => selected.key === file.key,
                                                        )}
                                                        onChange={(e) => handleFileSelection(file, e.target.checked)}
                                                    />
                                                </td>
                                                {/* ファイル名 */}
                                                <td className="h-px w-px whitespace-nowrap">
                                                    <div className="px-6 py-2">
                                                        <span>{file.key?.split("/").slice(2).join("/")}</span>
                                                    </div>
                                                </td>
                                                {/* 最終更新日時 */}
                                                <td className="h-px w-px whitespace-nowrap">
                                                    <div className="px-6 py-2">
                                                        <span>
                                                            {file.lastModified ? formatDateTime(file.lastModified) : ""}
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* データサイズ */}
                                                <td className="h-px w-px whitespace-nowrap">
                                                    <div className="px-6 py-2 text-right">
                                                        <span>
                                                            {file.size < 1024 * 1024 ? (
                                                                <span>
                                                                    {Number(
                                                                        (file.size / 1024).toFixed(1),
                                                                    ).toLocaleString()}{" "}
                                                                    KB
                                                                </span>
                                                            ) : (
                                                                <span>
                                                                    {Number(
                                                                        (file.size / (1024 * 1024)).toFixed(1),
                                                                    ).toLocaleString()}{" "}
                                                                    MB
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* ファイル操作 */}
                                                <td className="h-px w-px whitespace-nowrap">
                                                    <div className="px-2 py-2 text-center">
                                                        <ListIcon
                                                            fontSize="small"
                                                            className="cursor-pointer text-gray-600"
                                                            onClick={(e) => {
                                                                setSelectedFile(file);
                                                                setShowContextMenu(e);
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* プロジェクト削除ボタン（ファイルが空の場合のみ） */}
                                {filteredFileList.length == 0 && (
                                    <div className="mt-10 flex items-center justify-evenly">
                                        <button
                                            type="button"
                                            onClick={() => deleteFolder()}
                                            className="btn-primary w-[150px]"
                                        >
                                            {t("DataManagement.deleteProject")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* 戻るボタン */}
                <div className="mb-5 mt-10 flex items-center justify-evenly">
                    <button type="button" onClick={() => navigate(MYPAGE_ROUTE)} className="btn-secondary w-[150px]">
                        {t("DataManagement.backToMyPage")}
                    </button>
                </div>
            </div>
            {/* ファイル削除確認モーダル */}
            <S3FileDeleteConfirmModal
                showModal={showDeleteModal}
                setShowModale={setShowDeleteModal}
                selectedFile={selectedFile}
                handleDelete={deleteFile}
                isProcessing={isProcessing}
                errorMessage={deleteModalErrorMessage}
            />
            {/* 複数ファイル削除確認モーダル */}
            <S3FilesDeleteConfirmModal
                showModal={showDeleteFilesModal}
                setShowModal={setShowDeleteFilesModal}
                selectedFiles={selectedFiles}
                handleDelete={deleteFiles}
                isProcessing={isProcessing}
                errorMessage={deleteFilesModalErrorMessage}
            />
            {/* TIFF変換確認モーダル */}
            <TiffConvertConfirmModal
                showModal={showTiffConvertModal}
                closeShowTiffConvertModal={closeShowTiffConvertModal}
                selectedFile={selectedFile}
                handleConvert={convertTiff}
                isProcessing={isProcessing}
                errorMessage={tiffModalErrorMessage}
            />
            {/* 操作メニュー */}
            <ContextMenu
                show={showMenu}
                position={menuPosition}
                onClose={() => setShowMenu(false)}
                menuRef={menuRef}
                downloadFile={downloadFile}
                selectedFile={selectedFile}
                setShowShareFileModal={setShowShareFileModal}
                setShowTransfarFileBetweenProjectsModal={setShowTransfarFileBetweenProjectsModal}
                setShowDeleteModal={setShowDeleteModal}
                openShowTiffConvertModal={openShowTiffConvertModal}
            />
            {/* プロジェクトを共有モーダル */}
            <ShareProjectFileModal
                showModal={showShareProjectFileModal}
                handleClose={handleCloseShareProjectFileModal}
                selectedProject={selectedProject}
            />
            {/* ファイルを共有モーダル */}
            <ShareFileModal
                showModal={showShareFileModal}
                handleClose={handleCloseShareFileModal}
                selectedProject={selectedProject}
                selectedFile={selectedFile}
            />
            {/* プロジェクト間ファイル移動モーダル */}
            <TransfarFileBetweenProjectsModal
                showModal={showTransfarFileBetweenProjectsModal}
                handleClose={handleCloseTransfarFileBetweenProjectsModal}
                selectedProject={selectedProject}
                selectedFile={selectedFile}
                projectList={projectList}
                moveFileToOtherProject={moveFileToOtherProject}
                isProcessing={isProcessing}
                errorMessage={transfarModalErrorMessage}
                setErrorMessage={setTransfarModalErrorMessage}
            />
            {/* ストレージ使用量確認モーダル */}
            <CheckStorageUsageModal
                showModal={showCheckStorageUsageModal}
                handleClose={handleCloseCheckStorageUsageModal}
                userStorageMaxSizeGB={userStorageMaxSizeGB}
            />
            <Footer />
        </div>
    );
};

export default DataManagement;
